import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { colors } from '../styles';

export const COUNT_UP_MS = 1200;

interface IAnimatedPointsProps {
  value: number;
  /** Valor de onde a contagem parte; sem ele, mostra direto. */
  from?: number;
}

/**
 * Conta do valor antigo até o novo (FE-12). Enquanto conta, o número fica
 * laranja e cresce; um "+N" sobe e some, para quem olha de longe ver quanto
 * a equipe ganhou.
 */
const AnimatedPoints: React.FC<IAnimatedPointsProps> = ({ value, from }) => {
  const reduceMotion = useReducedMotion();
  const [shown, setShown] = useState(from ?? value);
  const frame = useRef<number>();
  const start = from ?? value;
  const changed = start !== value;

  useEffect(() => {
    if (!changed || reduceMotion) {
      setShown(value);
      return undefined;
    }

    const startedAt = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startedAt) / COUNT_UP_MS);
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(start + (value - start) * eased));
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [value, start, changed, reduceMotion]);

  if (!changed) return <strong>{shown} pts</strong>;

  const delta = value - start;

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <motion.strong
        style={{ display: 'inline-block', transformOrigin: 'center' }}
        initial={{ scale: 1, color: colors.text }}
        animate={
          reduceMotion
            ? { color: colors.accent }
            : {
                scale: [1, 1.25, 1.25, 1],
                color: [colors.text, colors.accent, colors.accent, colors.text],
              }
        }
        transition={{
          duration: (COUNT_UP_MS + 900) / 1000,
          times: [0, 0.15, 0.6, 1],
          ease: 'easeOut',
        }}
      >
        {shown} pts
      </motion.strong>
      {!reduceMotion && (
        <motion.span
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '100%',
            translateX: '-50%',
            padding: '0.1em 0.5em',
            borderRadius: 999,
            background: delta >= 0 ? colors.accent : colors.down,
            color: '#1a1a1a',
            fontFamily: 'RedHatDisplay-ExtraBold, RedHatDisplay-Bold',
            fontSize: '0.75em',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
          initial={{ opacity: 0, y: 12, scale: 0.6 }}
          animate={{
            opacity: [0, 1, 1, 0],
            y: [12, -4, -14, -34],
            scale: [0.6, 1.1, 1, 0.9],
          }}
          transition={{ duration: 2.2, times: [0, 0.18, 0.7, 1] }}
        >
          {delta >= 0 ? `+${delta}` : delta}
        </motion.span>
      )}
    </span>
  );
};

export default AnimatedPoints;
